import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import organisationsAPI from '@/api/organisations';
import { ErrorAlert } from '@/components/Alert';
import Button from '@/components/Button';
import Input from '@/components/Input';
import LayoutStandard from '@/components/LayoutStandard';
import AgreementNotice from '@/components/LoginPage/AgreementNotice';
import Divider from '@/components/LoginPage/OrContinueWithDivider';
import SocialLoginButtons from '@/components/LoginPage/SocialLoginButtons';
import MESSAGES from '@/constants/messages-en';
import { searchParamsQuery } from '@/helpers/urlHandler';
import { validateComplexPassword, validateEmail } from '@/helpers/validate';
import useAuth from '@/hooks/useAuth';

function SignUpPage() {
  const { signUp, login, loading, authError, user, orgID } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const activationKey = location.state?.activationKey;
  const { invite, inviteEmail, type } = searchParamsQuery(
    window.location.search,
  );

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [organisationName, setOrganisationName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const tandc = true;

  const [emailError, setEmailError] = useState(false);
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [organisationNameError, setOrganisationNameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const [tandcError, setTandcError] = useState(false);

  const capitalize = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  function onSignUp() {
    // set errors back to false
    setEmailError(false);
    setFirstNameError(false);
    setLastNameError(false);
    setOrganisationNameError(false);
    setPasswordError(false);
    setConfirmPasswordError(false);
    setTandcError(false);

    if (!validateEmail(email)) {
      setEmailError(true);
    } else if (firstName === '') {
      setFirstNameError(true);
    } else if (lastName === '') {
      setLastNameError(true);
    } else if (organisationName === '') {
      setOrganisationNameError(true);
    } else if (!validateComplexPassword(password)) {
      setPasswordError(true);
    } else if (password !== confirmPassword) {
      setConfirmPasswordError(true);
    } else if (!tandc) {
      setTandcError(true);
    } else {
      signUp(
        email,
        capitalize(firstName),
        capitalize(lastName),
        organisationName === ''
          ? [firstName, lastName].join(' ')
          : organisationName,
        password,
        confirmPassword,
        tandc,
        activationKey,
      );
    }
  }

  const acceptOrganisationInvite = () => {
    // set errors back to false
    setEmailError(false);
    setFirstNameError(false);
    setLastNameError(false);
    setPasswordError(false);
    setConfirmPasswordError(false);
    setTandcError(false);

    // Ignore email validation. because they're done server side
    if (firstName === '') {
      setFirstNameError(true);
    } else if (lastName === '') {
      setLastNameError(true);
    } else if (!validateComplexPassword(password)) {
      setPasswordError(true);
    } else if (password !== confirmPassword) {
      setConfirmPasswordError(true);
    } else if (!tandc) {
      setTandcError(true);
    } else {
      const params = {
        invite_code: invite,
        invite: {
          first_name: firstName,
          last_name: lastName,
          password,
          password_confirmation: confirmPassword,
        },
        type,
      };
      organisationsAPI
        .acceptUserInvite(params)
        .finally(() => login({ email: inviteEmail, password }));
    }
  };

  function isIOS() {
    return (
      [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod',
      ].includes(navigator.platform) ||
      // iPad on iOS 13 detection
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    );
  }

  // redirect if user is already logged in
  useEffect(() => {
    if (user && orgID) {
      navigate('/', { state: { activationKey } });
    }
  }, [activationKey, history, orgID, user]);

  return (
    <LayoutStandard
      heading="Create your Tapt account"
      backgroundColor="bg-gray-50"
    >
      <div
        className="sm:mx-auto sm:w-full sm:max-w-md lg:max-w-lg"
        style={{ maxHeight: undefined }}
      >
        <div className="bg-white py-2 px-4 sm:rounded-lg sm:px-10 sm:py-8 lg:border lg:border-gray-100">
          {authError && <ErrorAlert message={MESSAGES.error.auth} />}
          {emailError && <ErrorAlert message={MESSAGES.error.email} />}
          {firstNameError && <ErrorAlert message={MESSAGES.error.firstName} />}
          {lastNameError && <ErrorAlert message={MESSAGES.error.lastName} />}
          {organisationNameError && (
            <ErrorAlert message={MESSAGES.error.organisationName} />
          )}
          {passwordError && <ErrorAlert message={MESSAGES.error.password} />}
          {confirmPasswordError && (
            <ErrorAlert message={MESSAGES.error.passwordConfirm} />
          )}
          {tandcError && <ErrorAlert message={MESSAGES.error.tandc} />}

          <div className="space-y-6">
            <SocialLoginButtons />
            <Divider text="Or create your account" />
            <Input
              label="First name"
              type="text"
              value={firstName}
              required
              onChange={setFirstName}
              error={firstNameError}
            />
            <Input
              label="Last name"
              type="text"
              value={lastName}
              required
              onChange={setLastName}
              error={lastNameError}
            />

            {invite && inviteEmail ? (
              <Input
                label="Email address"
                type="email"
                value={inviteEmail}
                required
                onChange={setEmail}
                disabled
              />
            ) : (
              <>
                <Input
                  label="Email address"
                  type="email"
                  value={email}
                  required
                  onChange={setEmail}
                  onFocus={() => {
                    if (organisationName === '') {
                      setOrganisationName([firstName, lastName].join(' '));
                    }
                  }}
                  error={emailError}
                />
                <Input
                  label="Organisation name"
                  type="text"
                  value={organisationName}
                  required
                  onChange={setOrganisationName}
                  onFocus={() => {
                    if (organisationName === '') {
                      setOrganisationName([firstName, lastName].join(' '));
                    }
                  }}
                  error={organisationNameError}
                />
              </>
            )}

            <Input
              label="Password"
              type="password"
              value={password}
              required
              message="Min 8 chars. Min 1 uppercase letter. Min 1 number."
              onChange={setPassword}
              error={passwordError}
            />
            <Input
              label="Confirm password"
              type="password"
              value={confirmPassword}
              required
              onChange={setConfirmPassword}
              error={confirmPasswordError}
            />

            <AgreementNotice mode="signup" />

            <div>
              <Button
                buttonText="Create account"
                onClick={() => {
                  if (!loading) {
                    if (!invite) {
                      if (organisationName === '') {
                        setOrganisationName([firstName, lastName].join(' '));
                      }
                      onSignUp();
                    } else {
                      acceptOrganisationInvite();
                    }
                  }
                }}
                fullWidth
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="mt-8 text-center">
        <Link to="/login" state={{ activationKey }}>
          Sign in to Tapt
        </Link>
      </div>
    </LayoutStandard>
  );
}

export default SignUpPage;
